import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { LineNumber } from '@johnlewispartnership/wtr-website-trolley-sdk';
import {
  createApiTrolleyClient,
  createPendingTrolleyItemSessionStorageStore,
  createQueuedMutationTrolleyStrategy,
  createTrolleyService,
  isTrolleyClientAddTrolleyItemAuthenticationError,
  isTrolleyClientQueuedSupersededError,
  isTrolleyClientUpdateTrolleyItemAuthenticationError,
} from '@johnlewispartnership/wtr-website-trolley-sdk';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import type { TrolleyQuantity } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/TrolleyControls/types';

import config from 'config';
import TrolleyServiceClient from 'services/trolley';
import { useShoppingContext } from 'services/shopping-context/useShoppingContext';
import { redirectToLogin } from 'renderer/utils/navigate';

import { updateTrolleyCache, revertTrolleyItem, patchTrolleyItemId } from './updateTrolleyCache';
import TrolleyEventBus, {
  FINISHED_TROLLEY_UPDATE_EVENT,
  STARTED_TROLLEY_UPDATE_EVENT,
} from './trolley-event-bus';

export const TROLLEY_QUERY_KEY = 'wtr-cr/trolley-service';

export const trolleyQueryKeys = {
  trolley: (customerOrderId?: string) => [TROLLEY_QUERY_KEY, customerOrderId] as const,
  summary: (customerOrderId?: string) => [TROLLEY_QUERY_KEY, customerOrderId, 'summary'] as const,
};

export type CachedTrolleyItem = {
  trolleyItemId: string;
  lineNumber: string;
  quantity: TrolleyQuantity;
};

export type CachedTrolleyTotals = {
  totalEstimatedCost: number;
  itemTotalEstimatedCost: number;
  totalNumberOfItems: number;
  minimumSpendThresholdMet: boolean;
};

export type CachedTrolley = {
  trolleyItems: CachedTrolleyItem[];
  trolleyId: string;
  totals?: CachedTrolleyTotals;
};

const apiClientHeaders = {
  headers: {
    breadcrumb: config.applicationName,
  },
};

const toTrolleyUpdatePayload = (cached: CachedTrolley | undefined): CachedTrolleyTotals => ({
  itemTotalEstimatedCost: cached?.totals?.itemTotalEstimatedCost ?? 0,
  totalEstimatedCost: cached?.totals?.totalEstimatedCost ?? 0,
  totalNumberOfItems: cached?.totals?.totalNumberOfItems ?? 0,
  minimumSpendThresholdMet: cached?.totals?.minimumSpendThresholdMet ?? false,
});

const useTrolleyClient = () => {
  const { state } = useAuthentication();
  const { data: shoppingContextData } = useShoppingContext();
  const queryClient = useQueryClient();

  const token = state?.token;
  const isAuthResolved = state?.initialised ?? false;
  const isAuthenticated = state?.isLoggedIn ?? false;
  const customerOrderId = shoppingContextData?.customerOrderId;

  const trolleyService = useMemo(() => {
    /* Wraps unstash: fills in a missing trolleyId from the cache (re-stashing if still
     unknown) and optimistically adds the item so it appears immediately.
    */
    const sdkStore = createPendingTrolleyItemSessionStorageStore();
    const pendingStore = {
      stash: sdkStore.stash,
      unstash: async () => {
        const item = await sdkStore.unstash();

        if (!item) return null;

        const queryKey = trolleyQueryKeys.trolley(customerOrderId);
        const cachedTrolley = queryClient.getQueryData<CachedTrolley>(queryKey);
        const trolleyId = item.trolleyId || cachedTrolley?.trolleyId;

        if (!trolleyId) {
          await sdkStore.stash(item);
          return null;
        }

        if (
          cachedTrolley?.trolleyItems.some(cachedItem => cachedItem.lineNumber === item.lineNumber)
        ) {
          return null;
        }

        queryClient.setQueryData<CachedTrolley>(queryKey, old =>
          updateTrolleyCache(old, item.lineNumber, {
            amount: item.quantity.amount,
            uomId: item.quantity.uom,
          }),
        );

        return { ...item, trolleyId };
      },
    };

    return createTrolleyService({
      trolleyClient: createApiTrolleyClient({
        apiClient: TrolleyServiceClient,
        apiClientOptions: {
          getTrolley: { ...apiClientHeaders },
          getPricedTrolley: { ...apiClientHeaders },
          addTrolleyItem: { ...apiClientHeaders },
          updateTrolleyItem: { ...apiClientHeaders },
        },
        authenticationClient: {
          getAccessToken: () => token?.accessToken ?? '',
          isAuthenticated: () => isAuthenticated,
        },
      }),
      trolleyStrategy: createQueuedMutationTrolleyStrategy({
        onMutationsDrained: async () => {
          const queryKey = trolleyQueryKeys.trolley(customerOrderId);
          await queryClient.invalidateQueries({ queryKey });
          const fresh = queryClient.getQueryData<CachedTrolley>(queryKey);
          TrolleyEventBus.dispatch(FINISHED_TROLLEY_UPDATE_EVENT, {
            trolley: toTrolleyUpdatePayload(fresh),
          });
        },
      }),
      pendingTrolleyItemStore: pendingStore,
    });
  }, [token?.accessToken, isAuthenticated, queryClient, customerOrderId]);

  return { trolleyService, isAuthenticated, isAuthResolved, customerOrderId };
};

export const useTrolleyService = () => {
  const queryClient = useQueryClient();
  const { trolleyService, isAuthenticated, isAuthResolved, customerOrderId } = useTrolleyClient();
  const queryKey = useMemo(() => trolleyQueryKeys.trolley(customerOrderId), [customerOrderId]);

  const { data: summary } = useQuery({
    queryKey: trolleyQueryKeys.summary(customerOrderId),
    queryFn: async () => {
      const summaryResponse = await trolleyService.getTrolleySummary({
        customerOrderId: customerOrderId!,
      });

      // Safely seed the main queryKey so `unstash` and `reconcilePending` can find it
      queryClient.setQueryData<CachedTrolley>(queryKey, old => ({
        trolleyId: summaryResponse.trolleyId,
        trolleyItems: old?.trolleyItems ?? [],
        totals: old?.totals,
      }));

      return summaryResponse;
    },
    enabled: isAuthenticated && !!customerOrderId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 'static',
  });

  const { data: trolley, isFetched: isTrolleyFetched } = useQuery<CachedTrolley>({
    queryKey,
    queryFn: async () => {
      // Safe to run now, the main cache is seeded with trolleyId
      await trolleyService.reconcilePending();

      const pricedTrolley = await trolleyService.getPricedTrolley({
        trolleyId: summary!.trolleyId,
      });

      return {
        trolleyId: summary!.trolleyId,
        trolleyItems: pricedTrolley.items.map(item => ({
          trolleyItemId: item.id,
          lineNumber: item.lineNumber,
          quantity: { amount: item.quantity.amount, uomId: item.quantity.uom },
        })),
        totals: {
          totalEstimatedCost: pricedTrolley.totals.totalEstimatedPrice.amount,
          itemTotalEstimatedCost:
            pricedTrolley.totals.totalEstimatedPrice.amount -
            pricedTrolley.totals.deliveryCharge.amount,
          totalNumberOfItems: pricedTrolley.totals.totalNumberOfItems,
          minimumSpendThresholdMet: pricedTrolley.minimumSpendThresholdMet,
        },
      };
    },
    enabled: !!summary?.trolleyId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // isTrolleyFetched relies on the main query completing,
  // ensuring components wait for full pricing data to be ready.
  const isReady = isAuthResolved && (!isAuthenticated || isTrolleyFetched);

  const addItemMutation = useMutation({
    mutationFn: async ({
      quantity,
      lineNumber,
    }: {
      quantity: TrolleyQuantity;
      lineNumber: LineNumber;
    }) => {
      const currentTrolley = queryClient.getQueryData<CachedTrolley>(queryKey);

      return trolleyService.addTrolleyItem({
        // Empty when unauthenticated; reconciliation fills in the trolleyId later
        trolleyId: currentTrolley?.trolleyId ?? '',
        quantity: { amount: quantity.amount, uom: quantity.uomId },
        lineNumber,
      });
    },
    onMutate: async variables => {
      TrolleyEventBus.dispatch(STARTED_TROLLEY_UPDATE_EVENT);
      await queryClient.cancelQueries({ queryKey });

      // Snapshot only this line, so revert can't clobber another concurrent optimistic update
      const previousItem = queryClient
        .getQueryData<CachedTrolley>(queryKey)
        ?.trolleyItems.find(item => item.lineNumber === variables.lineNumber);

      queryClient.setQueryData<CachedTrolley>(queryKey, old =>
        updateTrolleyCache(old, variables.lineNumber, variables.quantity),
      );

      return { previousItem };
    },
    onError: (err, variables, context) => {
      if (isTrolleyClientQueuedSupersededError(err)) return;

      queryClient.setQueryData<CachedTrolley>(queryKey, old =>
        revertTrolleyItem(old, variables.lineNumber, context?.previousItem),
      );

      if (isTrolleyClientAddTrolleyItemAuthenticationError(err)) {
        redirectToLogin();
        return;
      }

      console.error('Failed to add trolley item:', err);

      const cached = queryClient.getQueryData<CachedTrolley>(queryKey);
      TrolleyEventBus.dispatch(FINISHED_TROLLEY_UPDATE_EVENT, {
        error: err,
        trolley: toTrolleyUpdatePayload(cached),
      });
    },
    onSuccess: (newItem, variables) => {
      queryClient.setQueryData<CachedTrolley>(queryKey, old =>
        patchTrolleyItemId(old, variables.lineNumber, newItem.id),
      );
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({
      quantity,
      lineNumber,
    }: {
      quantity: TrolleyQuantity;
      lineNumber: LineNumber;
    }) => {
      const { trolleyItems, trolleyId } = trolley ?? {};
      const existingItem = trolleyItems?.find(item => item.lineNumber === lineNumber);

      if (!existingItem || !trolleyId) {
        throw new Error(`Cannot update item: ${lineNumber} is not in the trolley.`);
      }

      if (quantity.amount === 0) {
        return trolleyService.removeTrolleyItem({
          trolleyId,
          trolleyItemId: existingItem.trolleyItemId,
          lineNumber,
        });
      }

      return trolleyService.updateTrolleyItem({
        trolleyId,
        trolleyItemId: existingItem.trolleyItemId,
        quantity: { amount: quantity.amount, uom: quantity.uomId },
        lineNumber,
      });
    },
    onMutate: async variables => {
      TrolleyEventBus.dispatch(STARTED_TROLLEY_UPDATE_EVENT);

      await queryClient.cancelQueries({ queryKey });

      const previousItem = queryClient
        .getQueryData<CachedTrolley>(queryKey)
        ?.trolleyItems.find(item => item.lineNumber === variables.lineNumber);

      queryClient.setQueryData<CachedTrolley>(queryKey, old =>
        updateTrolleyCache(old, variables.lineNumber, variables.quantity),
      );

      return { previousItem };
    },
    onError: (err, variables, context) => {
      if (isTrolleyClientQueuedSupersededError(err)) return;

      queryClient.setQueryData<CachedTrolley>(queryKey, old =>
        revertTrolleyItem(old, variables.lineNumber, context?.previousItem),
      );

      if (isTrolleyClientUpdateTrolleyItemAuthenticationError(err)) {
        redirectToLogin();
        return;
      }

      console.error('Failed to update trolley item:', err);

      const cached = queryClient.getQueryData<CachedTrolley>(queryKey);

      TrolleyEventBus.dispatch(FINISHED_TROLLEY_UPDATE_EVENT, {
        error: err,
        trolley: toTrolleyUpdatePayload(cached),
      });
    },
  });

  return {
    trolley,
    isReady,
    addTrolleyItem: (quantity: TrolleyQuantity, lineNumber: string) =>
      addItemMutation.mutate({ quantity, lineNumber }),
    updateTrolleyItem: (quantity: TrolleyQuantity, lineNumber: string) =>
      updateItemMutation.mutate({ quantity, lineNumber }),
  };
};
